import { useEffect } from 'react'
import type { FetchNextPageOptions } from '@tanstack/react-query'

/**
 * Cache rules for the palette's extra pages and scrolled windows: each is asked
 * for once per search (no retry, no refetch on focus or reconnect), and the
 * cache drops it a minute after the palette stops showing it.
 */
export const SEARCH_PAGE_QUERY_OPTIONS = {
  staleTime: 30_000,
  gcTime: 60_000,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const

/** Logs a failed page request, then rethrows it so its query fails. */
export function logFailure(message: string) {
  return (error: unknown): never => {
    console.error(message, error)
    throw error
  }
}

/** The parts of a manually driven (`enabled: false`) infinite query that load its pages. */
interface PagedQuery {
  data: { pages: unknown[] } | undefined
  hasNextPage: boolean
  isFetching: boolean
  isError: boolean
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<unknown>
}

/**
 * Loads an infinite query's pages one after another while `wanted` holds,
 * starting with its first page. A failed page stops the run until
 * `retryFailedPage` is called for the next request.
 */
export function useFetchPagesWhile(query: PagedQuery, wanted: boolean) {
  const { data, hasNextPage, isFetching, isError, fetchNextPage } = query
  const pageCount = data?.pages.length ?? 0
  const shouldFetch = wanted && (pageCount === 0 || hasNextPage) && !isFetching && !isError

  useEffect(() => {
    // pageCount re-runs this for each arriving page, even when the fetch settled
    // before a render ever saw it in flight.
    if (shouldFetch) void fetchNextPage({ cancelRefetch: false })
  }, [shouldFetch, pageCount, fetchNextPage])
}

/** Asks again for the page that failed, if one did. */
export function retryFailedPage(query: PagedQuery) {
  if (query.isError) void query.fetchNextPage({ cancelRefetch: false })
}
